// ignore-string-externalization

import React from 'react';
import styled from 'styled-components';
import {
  spacer8,
  spacer12,
  spacer16,
  spacer20,
  spacer24,
  spacer32,
  spacer80,
  screenSmMin,
  Type,
} from '@spotify-internal/encore-web';
import { ContentfulAsset } from '../../../Contentful';
import { Testimonial } from '../../SingleTestimonialModule/Testimonial';
import { ContentfulImage } from '../../../Contentful/ContentfulImage';
import { Box, ImageAspectBox1x1 } from '../../../ui';
import { ModuleType } from '../../types';

import { Colors } from './colorsType';

const Background = styled(Box)`
  background-color: ${(props: Colors) => props.backgroundColor};
  color: ${(props: Colors) => props.textColor};
  height: auto;
  &.tallest {
    height: 100%;
  }
`;

const HeroTestimonialStyled = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: ${(props: { centered: boolean }) =>
    props.centered ? 'center' : 'space-between'};
  @media (max-width: ${screenSmMin}) {
    flex-direction: column;
  }
`;

const HeroTestimonialWrapper = styled.div`
  padding-top: ${spacer20};
  flex-direction: column;
  display: flex;
  justify-content: center;
  text-align: ${(props: { centered: boolean }) =>
    props.centered ? 'center' : 'left'};

  @media (max-width: ${screenSmMin}) {
    padding-top: 0px;
  }
`;

const TestimonialWrapper = styled.div`
  width: ${(props: { centered: boolean }) => (props.centered ? '100%' : '65%')};
  @media (max-width: ${screenSmMin}) {
    width: 100%;
    padding-top: ${spacer12};
  }
`;

const ImageWrapper = styled(ImageAspectBox1x1)`
  width: 30%;
  display: flex;
  align-items: center;
  height: fit-content;
  align-self: center;

  @media (max-width: ${screenSmMin}) {
    width: 100%;
    margin-bottom: ${spacer8};
  }
`;

const StyledSupertext = styled(Type).attrs({
  variant: 'ballad',
  weight: 'bold',
})`
  padding-bottom: ${spacer16};
  text-transform: uppercase;
  @media (max-width: ${screenSmMin}) {
    padding-left: 0;
    padding: ${spacer12} 0px;
    width: 100%;
  }
`;

const StyledSupertextNoPadding = styled(StyledSupertext)`
  padding-bottom: 0px;
`;

const TestimonialNoImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const SupertextWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${screenSmMin}) {
    text-align: center;
  }
`;

const StyledTestimonial = styled.div`
  display: flex;
  align-items: center;
  width: 80%;

  @media (max-width: ${screenSmMin}) {
    width: 100%;
  }
`;

const CenterText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  width: 80%;

  @media (max-width: ${screenSmMin}) {
    width: 100%;
  }
`;

const TestimonialChar = styled(Type.h1).attrs({
  paddingBottom: '0.67em',
  variant: 'forte',
  weight: 'bold',
})`
 font-size ${spacer80};
 line-height: ${spacer16};
 padding-top: ${spacer32};
 padding-bottom: 0px;

 @media (max-width: ${screenSmMin}) {
    padding-top: ${spacer16};
  }
`;

// TODO: the font-size and line-heights below come directly from
// figma specs.  need to find a better way to do this
const TestimonialBody = styled(Type.p).attrs({
  paddingBottom: '0.75em',
  variant: 'alto',
  weight: 'book',
})`
  @media (max-width: ${screenSmMin}) {
    font-size: 18px; line-height: ${spacer24};
`;

const TestimonialSource = styled(Type.h1).attrs({
  variant: 'celloCanon',
  weight: 'bold',
})`
  padding-bottom: 0px;

  @media (max-width: ${screenSmMin}) {
    visibility: hidden;
  }
`;

const SourceTitle = styled(Type.p).attrs({
  variant: 'minuetBold',
})`
  padding-top: ${spacer8};

  @media (max-width: ${screenSmMin}) {
    visibility: hidden;
  }
`;

type HeroTestimonialType = {
  supertext?: string;
  body?: string;
  image?: ContentfulAsset;
  source?: string;
  jobTitle?: string;
  backgroundColor?: string;
  textColor?: string;
};

export const HeroTestimonial = React.forwardRef<
  React.MutableRefObject<ModuleType>,
  HeroTestimonialType
>(
  (
    {
      supertext = '',
      body = '',
      image,
      source = '',
      jobTitle = '',
      backgroundColor,
      textColor,
    },
    ref,
  ) => {
    const centered = image === undefined;
    return centered ? (
      <Background
        backgroundColor={backgroundColor}
        textColor={textColor}
        ref={ref as React.RefObject<HTMLDivElement>}
      >
        <TestimonialNoImageWrapper>
          <SupertextWrapper>
            <StyledSupertextNoPadding>{supertext}</StyledSupertextNoPadding>
          </SupertextWrapper>
          <StyledTestimonial>
            <Testimonial
              quote={body}
              source={source}
              jobTitle={jobTitle}
              centered={centered}
              isHero
            />
          </StyledTestimonial>
        </TestimonialNoImageWrapper>
      </Background>
    ) : (
      <Background
        backgroundColor={backgroundColor}
        textColor={textColor}
        ref={ref as React.RefObject<HTMLDivElement>}
      >
        <HeroTestimonialWrapper centered={centered}>
          <HeroTestimonialStyled centered={centered}>
            {image && (
              <ImageWrapper>
                <ContentfulImage asset={image} />
              </ImageWrapper>
            )}
            <TestimonialWrapper centered={centered}>
              <CenterText>
                <StyledSupertext>{supertext}</StyledSupertext>
                <TestimonialChar>“</TestimonialChar>
                <TestimonialBody>{body}</TestimonialBody>
                <TestimonialSource> &mdash; {source}</TestimonialSource>
                <SourceTitle>{jobTitle}</SourceTitle>
              </CenterText>
            </TestimonialWrapper>
          </HeroTestimonialStyled>
        </HeroTestimonialWrapper>
      </Background>
    );
  },
);
